import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePhonePlansAvailableToChange } from '@/hooks/usePlans';
import type { MobileLine } from '@octave/contracts';
import { AlertCircle, Clock, Loader2 } from 'lucide-react';
import { useState } from 'react';

export function ChangePlanDialog({
  isOpen,
  onClose,
  onConfirm,
  currentPlan,
  organizationId,
  isUpgrading,
  lineDetails,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (planId: string) => void;
  currentPlan: MobileLine['phonePlan'];
  organizationId: string;
  isUpgrading: boolean;
  lineDetails: MobileLine;
}) {
  const [selectedPlan, setSelectedPlan] = useState(currentPlan.id);
  const [show5GWarning, setShow5GWarning] = useState(false);

  const {
    data: phonePlansResponse,
    isLoading,
    error,
  } = usePhonePlansAvailableToChange(currentPlan.id, organizationId);

  const isUpgrade = () => {
    const current =
      phonePlansResponse?.find((p) => p.id === currentPlan.id)?.dataLimitMo ||
      0;
    const selected =
      phonePlansResponse?.find((p) => p.id === selectedPlan)?.dataLimitMo || 0;
    return selected > current;
  };

  const has5GActive = lineDetails?.packageOptions?.find(
    (option) => option.label === 'fiveG' && option.active
  );

  const selectedPlanData =
    phonePlansResponse?.find((p) => p.id === selectedPlan)?.dataLimitMo || 0;
  const isIncompatible5G = has5GActive && selectedPlanData < 1024;

  const handlePlanSelection = (planId: string) => {
    const planData =
      phonePlansResponse?.find((p) => p.id === planId)?.dataLimitMo || 0;
    const isIncompatible = has5GActive && planData < 1024;

    if (isIncompatible) {
      setShow5GWarning(true);
    } else {
      setSelectedPlan(planId);
      setShow5GWarning(false);
    }
  };

  const handleConfirm = () => {
    if (isIncompatible5G) {
      setShow5GWarning(true);
    } else {
      onConfirm(selectedPlan);
    }
  };

  if (isLoading || error) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[600px] p-6">
          <div className="flex items-center justify-center p-8">
            {isLoading ? (
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Chargement des forfaits...
                </p>
              </div>
            ) : (
              <div className="text-center space-y-4 text-red-500">
                <AlertCircle className="h-8 w-8 mx-auto" />
                <p className="text-sm">
                  Une erreur est survenue lors du chargement des forfaits.
                </p>
                <Button variant="outline" onClick={onClose}>
                  Fermer
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[800px] p-6 pr-8 max-h-[85vh]">
          <DialogHeader className="p-0 pb-3 border-b">
            <div className="flex items-center justify-between mb-2">
              <DialogTitle className="text-xl">Changer de forfait</DialogTitle>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/5 rounded-full border border-primary/10">
                <span className="text-sm font-medium text-primary">
                  {currentPlan.operator.name}
                </span>
              </div>
            </div>
            <DialogDescription>
              Sélectionnez le nouveau forfait pour cette ligne.
            </DialogDescription>
          </DialogHeader>

          {/* Info Banner */}
          <div className="px-4 py-2 bg-muted/5 border-b">
            <div className="flex flex-col gap-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-500" />
                <span>
                  Changement à la hausse : opérationnel en ~15 minutes
                </span>
              </div>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-blue-500" />
                <span>
                  Changement à la baisse : opérationnel au 1er jour du mois
                  suivant
                </span>
              </div>
            </div>
          </div>

          {/* Plans Lists */}
          <div className="p-4 overflow-y-auto" style={{ maxHeight: '40vh' }}>
            <div className="grid grid-cols-2 gap-3">
              {phonePlansResponse?.map((plan) => {
                const planData = plan.dataLimitMo || 0;
                const isIncompatible = has5GActive && planData < 1024;
                const isSelected = selectedPlan === plan.id;

                return (
                  <div
                    key={plan.id}
                    className={`relative flex items-start p-4 rounded-lg border transition-all cursor-pointer
                          ${
                            isSelected
                              ? 'border-primary bg-primary/5 ring-1 ring-primary'
                              : isIncompatible
                                ? 'border-red-200 bg-gradient-to-br from-red-50 to-red-100/50 opacity-80 cursor-not-allowed shadow-sm'
                                : 'hover:border-primary/50 hover:shadow-md'
                          }
                        `}
                    onClick={() =>
                      !isIncompatible && handlePlanSelection(plan.id)
                    }
                  >
                    <input
                      type="radio"
                      id={plan.id}
                      name="plan"
                      value={plan.id}
                      checked={isSelected}
                      onChange={(e) =>
                        !isIncompatible && handlePlanSelection(e.target.value)
                      }
                      className="h-4 w-4 mt-1"
                      disabled={isIncompatible}
                    />
                    <label
                      htmlFor={plan.id}
                      className="ml-3 cursor-pointer flex-1"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-lg font-medium">{plan.name}</span>
                        <div className="flex items-center gap-1.5">
                          {currentPlan.id === plan.id && (
                            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                              Actuel
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">
                          {plan.isDataOnly
                            ? 'Forfait data uniquement'
                            : 'Voix/SMS/MMS'}
                        </p>
                        <p className="text-sm font-medium text-primary">
                          {(plan.price / 100).toFixed(2)} € / mois
                        </p>
                        {isIncompatible && (
                          <div className="flex items-center gap-1.5 mt-1.5 p-2 bg-red-50 border border-red-100 rounded-md">
                            <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
                            <p className="text-xs font-medium text-red-700">
                              Désactivez d'abord l'option 5G pour sélectionner
                              ce forfait
                            </p>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-muted/5">
            <div className="flex items-center justify-between">
              {selectedPlan !== currentPlan.id && (
                <p className="text-sm text-muted-foreground">
                  Changement effectif{' '}
                  {isUpgrade()
                    ? 'dans environ 15 minutes'
                    : 'au 1er jour du mois prochain'}
                </p>
              )}
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" onClick={onClose}>
                  Annuler
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={
                    selectedPlan === currentPlan.id ||
                    isUpgrading ||
                    isIncompatible5G
                  }
                >
                  {isUpgrading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Confirmer le changement
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 5G Warning Dialog */}
      <Dialog open={show5GWarning} onOpenChange={setShow5GWarning}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-orange-700">
              <div className="p-2 bg-orange-100 rounded-full">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              Attention - Incompatibilité 5G
            </DialogTitle>
            <DialogDescription className="text-left space-y-3">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm font-medium text-orange-800 mb-2">
                  Le forfait sélectionné est incompatible avec l'option 5G
                  actuellement détenue.
                </p>
                <p className="text-sm text-orange-700">
                  Pour pouvoir poursuivre le changement de forfait, merci de
                  décocher l'option 5G actuellement détenue puis de réaliser le
                  changement de forfait.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShow5GWarning(false)}>
              Annuler
            </Button>
            <Button
              variant="default"
              className="bg-orange-600 hover:bg-orange-700"
              onClick={() => {
                setShow5GWarning(false);
              }}
            >
              Gérer les options
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
